import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Plus } from 'lucide-react';
import type { EquipmentServiceRate } from '@jobos/shared';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { dateToLocalISO } from '@/lib/format';
import type { EquipmentVisitUI } from './equipment-helpers';

const money = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

export interface AddVisitInput {
  rateKey: string;
  dates: string[];
  qty: number;
  editingId?: string;
}

export interface AddVisitPopoverProps {
  rates: EquipmentServiceRate[];
  onAdd: (input: AddVisitInput) => void;
  disabled?: boolean;
  editingVisit?: EquipmentVisitUI;
  trigger?: ReactNode;
}

export function AddVisitPopover({
  rates,
  onAdd,
  disabled = false,
  editingVisit,
  trigger,
}: AddVisitPopoverProps) {
  const isEditing = !!editingVisit;
  const [open, setOpen] = useState(false);
  const sorted = useMemo(
    () => [...rates].sort((a, b) => a.sortOrder - b.sortOrder),
    [rates],
  );

  const [rateKey, setRateKey] = useState<string>(
    editingVisit?.rateKey ?? sorted[0]?.key ?? '',
  );
  // A visit carries a single optional date. Legacy multi-date visits seed from
  // their first date; saving collapses them to that one date.
  const [date, setDate] = useState(editingVisit?.dates[0] ?? '');

  useEffect(() => {
    if (!open) return;
    if (isEditing && editingVisit) {
      setRateKey(editingVisit.rateKey);
      setDate(editingVisit.dates[0] ?? '');
    } else {
      setRateKey(sorted[0]?.key ?? '');
      setDate('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selected = sorted.find((r) => r.key === rateKey);
  const total = selected ? Number(selected.rate) : 0;
  const canSubmit = !!selected;

  const handleAdd = () => {
    if (!canSubmit || !selected) return;
    const payload: AddVisitInput = {
      rateKey: selected.key,
      dates: date ? [date] : [],
      qty: 1,
    };
    if (isEditing && editingVisit) {
      payload.editingId = editingVisit.id;
    }
    onAdd(payload);
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger ?? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled || sorted.length === 0}
            className="h-8 text-sm text-emerald-700 hover:text-emerald-800"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add visit
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[400px] p-4">
        <h4 className="text-sm font-semibold mb-3">
          {isEditing ? 'Edit service visit' : 'Add service visit'}
        </h4>

        <div className="space-y-3">
          <div>
            <label className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">
              Service
            </label>
            <select
              aria-label="Service"
              value={rateKey}
              onChange={(e) => setRateKey(e.target.value)}
              disabled={isEditing}
              className="w-full text-sm border rounded-md px-2 py-1.5 bg-white h-8 disabled:opacity-60 disabled:bg-gray-50"
            >
              {sorted.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.label} — {money(Number(r.rate))}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">
              Date <span className="normal-case text-gray-400">(optional)</span>
            </label>
            <DatePicker
              aria-label="Date"
              value={date || null}
              onChange={(d) => setDate(d ? dateToLocalISO(d) : '')}
              placeholder="Optional"
              className="h-8 text-sm"
            />
          </div>

          {selected && (
            <div className="text-xs text-gray-600 bg-emerald-50/50 rounded px-2 py-1.5 flex items-center justify-between">
              <span>1 × {money(Number(selected.rate))}</span>
              <span className="text-emerald-700 font-semibold tabular-nums">
                {money(total)}
              </span>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleAdd} disabled={!canSubmit}>
              {isEditing ? 'Save' : 'Add'}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
